import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/forms/checkbox";
import IconButton from "@/components/ui/icon-button";
import { MenuTrigger } from "@/components/ui/menu";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  CaretDownIcon,
  DotsThreeIcon,
  SpinnerGapIcon,
} from "@phosphor-icons/react";
import {
  type FC,
  type HTMLAttributes,
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface FilterComponentItemProps extends HTMLAttributes<HTMLElement> {
  title: string;
  count?: number;
  isCountLoading?: boolean;
  selected: boolean;
  tabIndex: number;
  toggleFilter: () => void;
  /** Show expand/collapse chevron and call when toggled */
  expand?: boolean;
  isExpanded?: boolean;
  handleExpandClick?: (isExpanded: boolean) => void;
  countClassNames?: string;
  labelTextWrap?: "truncate" | "wrap";
  children?: ReactNode;
}

const styles = {
  container: cn(
    "group flex contain-layout px-1.5 py-[2px] text-xxs cursor-pointer rounded-xs",
    "hover:bg-slate-300/10 will-change-[background-color] transition-colors",
  ),
  label: cn(
    "min-w-0 w-full relative p-0 text-xxs text-left font-normal border-0 bg-transparent data-hovered:bg-transparent",
  ),
  count: cn(
    "shrink-0 ml-2.5 text-right text-gray-600 transition-opacity duration-200",
  ),
  iconButton: cn(
    "z-100 absolute right-0 top-1/2 -translate-y-1/2 h-5 ml-2 px-2 py-0 p-0.5",
    "opacity-0 group-hover:opacity-100 data-hovered:bg-gray-200 will-change-[opacity,transform] transition-opacity",
  ),
  expandButton: cn("z-100 h-5 w-5 p-0.5 data-hovered:bg-gray-200"),
};

const FilterComponentItem: FC<FilterComponentItemProps> = memo(
  ({
    title,
    count,
    isCountLoading,
    selected,
    tabIndex,
    toggleFilter,
    className,
    children,
    expand,
    isExpanded,
    handleExpandClick,
    countClassNames,
    labelTextWrap = "truncate",
  }) => {
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);
    const resizeObserver = useRef<ResizeObserver | null>(null);
    const menuTriggerRef = useRef<HTMLButtonElement>(null);

    const checkTruncation = useCallback(() => {
      if (textRef.current) {
        const overflow =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTextTruncated(overflow);
      }
    }, []);

    useEffect(() => {
      if (labelTextWrap !== "truncate") {
        setIsTextTruncated(false);
        resizeObserver.current?.disconnect();
        resizeObserver.current = null;
        return;
      }
      checkTruncation();
      const el = textRef.current;
      if (el) {
        const obs = new ResizeObserver(checkTruncation);
        obs.observe(el);
        resizeObserver.current = obs;
      }
      return () => {
        resizeObserver.current?.disconnect();
        resizeObserver.current = null;
      };
    }, [labelTextWrap, title, checkTruncation]);

    const handleContextMenu = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (children) {
          e.preventDefault();
          e.stopPropagation();
          menuTriggerRef.current?.click();
          setIsMenuOpen(true);
        }
      },
      [children],
    );

    const isTruncateMode = labelTextWrap === "truncate";
    const isWrapMode = labelTextWrap === "wrap";
    const labelPadding = expand ? "pr-14" : "";

    const labelContent = (
      <span
        ref={textRef}
        className={cn(
          "text-gray-600",
          isTruncateMode &&
            "block overflow-hidden text-ellipsis whitespace-nowrap leading-4",
          isWrapMode &&
            "block whitespace-normal wrap-break-word m-0 p-0 leading-4",
          title === "Ungrouped" && "italic",
        )}
      >
        {title}
      </span>
    );

    const labelButton = (
      <Button
        type="button"
        variant="none"
        size="label"
        aria-label={`Filter by ${title}`}
        onPress={toggleFilter}
        className={cn(
          styles.label,
          isTruncateMode && isTextTruncated && "pr-12",
          isWrapMode && "justify-start! items-start! text-left! h-auto!",
          labelPadding,
        )}
      >
        {labelContent}
      </Button>
    );

    const wrappedLabel =
      isTruncateMode && isTextTruncated ? (
        <TooltipTrigger>
          {labelButton}
          <Tooltip variant="accessibility" align="left">
            {title}
          </Tooltip>
        </TooltipTrigger>
      ) : (
        labelButton
      );

    return (
      <div
        className={cn(
          styles.container,
          labelTextWrap === "wrap" && "items-start!",
          className,
        )}
        tabIndex={0}
        role="menuitem"
        aria-label={`${title} filter item`}
        onContextMenu={handleContextMenu}
      >
        <Checkbox
          aria-label={`Select ${title} filter`}
          checked={selected}
          tabIndex={tabIndex}
          variant="secondary"
          className={cn(
            "mr-1",
            labelTextWrap === "wrap" ? "self-start" : "self-center",
          )}
          onClick={toggleFilter}
        />
        {expand && (
          <IconButton
            icon={<CaretDownIcon size={12} />}
            aria-label="Expand/Collapse"
            variant="ghost"
            className={cn(styles.expandButton, isExpanded && "rotate-180")}
            onPress={() => handleExpandClick?.(!isExpanded)}
          />
        )}
        <div
          className={cn(
            "flex-1 h-auto leading-none min-w-0 relative flex",
            labelTextWrap === "wrap"
              ? "items-start justify-start self-start pt-0"
              : "justify-center items-center",
          )}
        >
          {wrappedLabel}
        </div>
        {count !== undefined && (
          <output
            className={cn(
              styles.count,
              countClassNames,
              children && "group-hover:opacity-0",
              isMenuOpen && children && "opacity-0",
            )}
            aria-label={`${count.toLocaleString()} items`}
          >
            {isCountLoading ? (
              <SpinnerGapIcon className="animate-spin" size={12} />
            ) : (
              count.toLocaleString()
            )}
          </output>
        )}
        {children && (
          <MenuTrigger onOpenChange={setIsMenuOpen}>
            <IconButton
              ref={menuTriggerRef}
              icon={<DotsThreeIcon size={20} />}
              aria-label="More options"
              className={cn(
                styles.iconButton,
                isMenuOpen ? "opacity-100" : "opacity-0",
              )}
              variant="ghost"
              onPressChange={() => setIsMenuOpen((prev) => !prev)}
            />
            {children}
          </MenuTrigger>
        )}
      </div>
    );
  },
);

FilterComponentItem.displayName = "FilterComponentItem";

export default FilterComponentItem;
